<template>
  <div class="ba-page-capture"
       :class="[{ heatmap : isHeatmapRun }, `step${step}`]"
       v-show="isOpen">
    <transition name="fade">
      <div class="step-1"
           v-show="step === 1 && !capturing">
        <i class="icon icon--camera-size70"></i>
        <h3 v-html="$t('journey.capture.title')"></h3>
        <h4 v-html="$t('journey.capture.description1')"></h4>
        <p v-html="$t('journey.capture.description2')"></p>
        <div class="buttons">
          <ui-button :primary="true"
                     :iconName="'icon--link-chain'"
                     @click="onSelectType('url')">
            {{ $t('journey.capture.url.title' )}}
          </ui-button>
          <ui-button class="upload"
                     :primary2="true"
                     :disabled="isHeatmapRun"
                     :iconName="'icon--link-upload-red'"
                     @click="onSelectType('upload')">
            {{ $t('journey.capture.upload.title' )}}
          </ui-button>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div class="step-2"
           :class="{ heatmap : isHeatmapRun }"
           v-show="step === 2 && !capturing">
        <div class="title--wrap" :class="{ heatmap : isHeatmapRun }">
          <h3 ref="title">{{ $t('journey.capture.title' )}}</h3>
          <span class="url" ref="url" :title="captureDisplayUrl">{{ captureDisplayUrl }}</span>
        </div>
        <div class="select-type"
             :class="{ right: type === 'upload', heatmap : isHeatmapRun }">
          <button type="button"
                  :class="{ on: type === 'url' }"
                  @click="onSelectType('url')">
            <i class="icon icon--check-white-size20"></i>
            {{ $t('journey.capture.url.title' )}}
          </button>
          <button type="button"
                  :disabled="isHeatmapRun"
                  :class="{ on: type === 'upload', disabled: isHeatmapRun }"
                  @click="onSelectType('upload')">
            <i class="icon icon--check-white-size20"></i>
            {{ $t('journey.capture.upload.title' )}}
          </button>
        </div>

        <div class="capture-type" :class="{ heatmap : isHeatmapRun }">
          <div v-if="IsCaptureProcessing"></div>
          <transition name="fade">
            <heatmap-capture
              v-if="isHeatmapRun && type === 'url'"
              :bLogin="bLogin"
              :cssSelector="cssSelector"
              :model="model"
              :modelError="modelError"
              @onSelectLoginAction="onSelectLogin"
              @onSelectCssSelectorAction="onSelectCssSelector"
              @inputSelector="onInputSelector"
              @excludeCheckChange="excludeCheckChange">
            </heatmap-capture>
          </transition>

          <transition name="fade">
            <journey-capture
              v-if="!isHeatmapRun && type === 'url'"
              :type="type"
              :urlType="urlType"
              :model="model"
              :modelError="modelError"
              @onCaptureAction="onCapture"
              @onSelectUrlTypeAction="onSelectUrlType">
            </journey-capture>
          </transition>

          <transition name="fade">
            <div class="upload"
                 :class="[`process-${imageProcess}`, device]"
                 v-show="type === 'upload'">

                <div class="upload-area">
                  <div class="format" v-if="imageProcess >= -1 && imageProcess < 101">
                    <div class="extension">
                      <h4>{{ $t('journey.capture.upload.allowedExtension') }}</h4>
                      <ul>
                        <li>JPEG, PNG, BMP</li>
                      </ul>
                    </div>
                    <div class="size"
                         :class="device">
                      <p>
                        {{ thumbnailRatio[device].width }} : {{ thumbnailRatio[device].height }}
                      </p>
                    </div>
                  </div>
                  <vue-dropzone ref="dropzone"
                                id="dropzone"
                                @vdropzone-file-added="onImageProcessing"
                                @vdropzone-removed-file="onImageRemoved"
                                @vdropzone-thumbnail="onImageProcessed"
                                @vdropzone-max-files-exceeded="onMaxFilesExceeded"
                                @vdropzone-sending="onUploading"
                                :options="dropzoneOptions"
                                :useCustomSlot="true">
                    <div class="drop-area">
                      <div class="drop">
                        <i class="icon icon--drop-here"></i>
                        <p v-html="$t('journey.capture.upload.dropFileOr')"></p>
                        <ui-button :size="'small'">{{ $t('journey.capture.upload.findFile') }}</ui-button>
                      </div>
                    </div>
                    <div class="image-processing">
                      <div class="loader" v-if="imageProcess !== -1">
                        <div class="value"
                             :style="{ left: `${imageProcess}%` }">
                          <ba-loading :absolutePosition="false" />
                          <span>{{ imageProcess | max(100) }}%</span>
                        </div>
                        <div class="bar"
                             :style="{ width: `${imageProcess}%` }"></div>
                        <i class="close icon icon--close-size16"
                           @click="onStopProcessing"></i>
                      </div>
                      <div v-else class="error-message" >
                        <i class="icon icon--drop-here"></i>
                        <p v-html="$t('journey.capture.upload.errorMessage')"></p>
                        <ui-button :size="'small'">{{ $t('journey.capture.upload.findFile') }}</ui-button>
                      </div>

                    </div>
                  </vue-dropzone>
                </div>

              <div class="buttons">
                <ui-button :primary2="true"
                           :disabled="!hasImage"
                           @click="onUpload">{{ $t('common.confirm') }}</ui-button>
              </div>
            </div>
          </transition>
        </div>

        <div class="capture-buttons" v-if="type === 'url'" :class="{ heatmap : isHeatmapRun }">
          <ui-button :primary2="true"
                     :disabled="(excludeCheck && !cssSelector) || (model.captureUrl.length < 1)"
                     @click="onCaptureSelect">{{ $t('common.confirm') }}</ui-button>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div class="step-3"
           v-show="capturing">
        <div class="capturing">
          <ba-loading :absolutePosition="false" />
          <p v-if="type === 'url'"
             class="title"
             v-html="$t('journey.capture.url.taking')"></p>
          <p v-else
             class="title"
             v-html="$t('journey.capture.upload.uploading')"></p>

          <div class="loading--wrap">
            <i class='loading-bar'></i>
            <i class='loading-bar'></i>
            <i class='loading-bar'></i>
            <i class='loading-bar'></i>
            <i class='loading-bar'></i>
          </div>

          <p v-if="type === 'url'"
             class="desc"
             v-html="$t('journey.capture.url.takingDescription')"></p>
          <p v-else
             class="desc"
             v-html="$t('journey.capture.upload.uploadingDescription')"></p>
        </div>
      </div>
    </transition>

    <i class="close-layer icon icon--close-size16" @click="onCloseLayer"></i>
  </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import { mapState } from 'vuex'
import { getS3Url, getS3CaptureType, getFromPageKey, isMatchedURL } from '@/libs/utils'
import { requestCapture } from '@/api/capture'
import { getAuthToken } from '@/libs/auth-helper'
import { baseUrl } from '@/libs/api-request'
import { isValidUrl } from '@/libs/validation-helper'
import { updateBaApply, requestCapture as heatmapRequestCapture } from '@/api/heatmap'

import JourneyCapture from '@/components/journey/JourneyCapture'
import HeatmapCapture from '@/components/journey/HeatmapCapture'

import UiButton from '@/components/ui/UiButton'
import BaLoading from '@/components/ui/ba/Loading'
import { EventBus } from '@/libs/event-bus'

export default {
  name: 'BaCapture',
  components: {
    UiButton,
    BaLoading,
    JourneyCapture,
    HeatmapCapture,
    vueDropzone: vue2Dropzone
  },
  watch: {
    step () {
      window.setTimeout(() => {
        this.resizeTitleUrl()
      }, 500)
    }
  },
  computed: {
    ...mapState([
      'journeyOptions'
    ]),
    captureDisplayUrl () {
      if (!this.pageData.linkUrl) return this.pageData.url || ''
      const protocol = this.pageData.linkProtocol
        ? `${this.pageData.linkProtocol}//`
        : 'https://'
      return `${protocol}${this.pageData.linkUrl}`
    },
    computedData () {
      if (this.journeyOptions.matchType !== 'exact' && this.pageData.key && typeof this.pageData.key === 'string' && this.pageData.key.indexOf('|') > -1) {
        // trend , targeting
        return {
          ...getFromPageKey(this.pageData.key),
          url: this.pageData.url
        }
      } else {
        // ranking , journey
        return {
          ...this.pageData,
          // sid: this.journeyOptions.sid,
          urlId: this.pageData.gUrlId || this.pageData.key, // key -> journey
          sid: this.journeyOptions.sid,
          matchType: this.journeyOptions.matchType
        }
      }
    },
    device () {
      return this.capturingDevice || this.journeyOptions.device
    },
    imagePath () {
      return this.pageData.imagePath ? this.pageData.imagePath[this.device] : null
    },
    captureType () {
      return getS3CaptureType(this.imagePath)
    },
    realCaptureUrl () {
      if (this.pageData.realCaptureUrl) {
        return this.pageData.realCaptureUrl[this.device] || this.pageData.url
      }
      return ''
    },
    dropzoneOptions () {
      return {
        url: baseUrl() + '/ba/capture/upload',
        thumbnailWidth: 10000000,
        thumbnailHeight: 10000000,
        resizeMethod: 'crop',
        resizeMimeType: 'image/png',
        resizeWidth: this.thumbnailSize[this.device].width,
        resizeHeight: this.thumbnailSize[this.device].height,
        maxFiles: 1,
        autoProcessQueue: false,
        acceptedFiles: 'image/jpeg,image/png,image/bmp',
        addRemoveLinks: true,
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        }
      }
    },
    isHeatmapRun () {
      return !!(this.pageData.baApply && this.pageData.baApply.state === 'run')
    },
    aid () {
      return this.pageData.baApply._id
    },
    IsCaptureProcessing () {
      let label = false
      for (let index = 0; index < this.wholeCaptureProcessing.length; index++) {
        if ((this.wholeCaptureProcessing[index]['aid'] === this.aid) && (this.wholeCaptureProcessing[index]['state'] === null)) {
          label = true
        } else {
          if (this.wholeCaptureProcessing[index]['state'] === 'success') {
            this.captureEnded(index)
          } else {
            this.captureEnded(index)
          }
          label = false
        }
      }
      return label
    }
  },
  methods: {
    onSelectType (value) {
      if (this.isHeatmapRun && this.step === 2) {
        return null
      }
      if (value === 'url') {
        if (!this.realCaptureUrl || this.pageData.url === this.realCaptureUrl) {
          this.onSelectUrlType('default')
        } else {
          this.onSelectUrlType('custom')
        }
      } else {
        this.hasImage = false
        this.imageProcess = 0
      }
      this.type = value
      this.step = 2
    },
    onSelectUrlType (value) {
      if (value === 'default') {
        this.model.captureUrl = this.pageData.url
        if (this.pageData.baApply) {
          this.model.cssSelector = this.pageData.baApply.exceptionSelector
        }
      } else if (value === 'custom') {
        this.model.captureUrl = this.realCaptureUrl || this.pageData.url
      }
      this.urlType = value
    },
    onSelectLogin (value) {
      this.bLogin = value
      this.model.captureUrl = this.pageData.url
    },
    onSelectCssSelector (value) {
      this.cssSelector = value
      this.model.cssSelector = ''
    },
    onInputSelector (value) {
      this.cssSelector = value
    },
    excludeCheckChange (value) {
      this.excludeCheck = value
      if (value && !this.cssSelector) {
        this.modelError.cssSelector = 'requireCssSelector'
      }
    },
    onCaptureSelect () {
      let apiParam = {}
      apiParam.captureURL = this.model.captureUrl
      apiParam.bLogin = this.bLogin
      if (this.excludeCheck) {
        apiParam.exceptionSelector = this.cssSelector
      } else {
        apiParam.exceptionSelector = ''
      }
      if (this.isHeatmapRun) {
        this.onHeatmapCapture(apiParam)
      } else {
        this.onCapture()
      }
    },
    onCapture () {
      if (!this.model.captureUrl) {
        this.modelError.captureUrl = 'requiredUrl'
      } else {
        const data = this.computedData
        const captureUrl = this.model.captureUrl

        requestCapture(data.sid, data.urlId, data.matchType, data.url, this.device, captureUrl)
          .then(result => {
            this.capturing = true
            this.step = 3
            // const key = this.pageData.key || `${data.urlId}-${this.device}-${data.sid}`
            this.$emit('onCaptureProcessing', this.computedData)
            setTimeout(this.onCloseLayer, 5000)
          })
          .catch(() => {
            this.modelError.captureUrl = 'notValidUrl'
          })
      }
    },
    onHeatmapCapture (apiParam) {
      const nodeHost = this.pageData.linkHost
      const nodeTarget = this.pageData.linkPath
      if (!isMatchedURL(nodeHost, nodeTarget, this.model.captureUrl, this.journeyOptions.matchType)) {
        this.modelError.captureUrl = 'unAbleDomain'
        return false
      }
      if (!isValidUrl(this.model.captureUrl)) {
        this.modelError.captureUrl = 'unAbleDomain'
        return false
      } else {
        this.modelError = ''
        const payload = {
          id: this.aid,
          ...apiParam
        }

        updateBaApply(payload).then(result => {
          EventBus.$emit('updateBaApply', result)
          if (result && !this.bLogin) {
            const params = `/${this.aid}`
            heatmapRequestCapture(params).then(result => {
              if (result.data) {
                let arr = this.wholeCaptureProcessing
                arr.push({
                  aid: this.aid,
                  requestId: result.data.requestId,
                  uuid: result.data.uuid,
                  state: null
                })
                this.$emit('onCaptureProcessing', this.computedData)
                this.$emit('onWholeCaptureProcessingChange', arr)
                this.capturing = true
                this.step = 3
                setTimeout(this.onCloseLayer, 5000)
              }
            }).catch((err) => {
              console.log(err)
            })
          } else {
            this.onCloseLayer()
          }
        })
      }
    },
    onCloseLayer () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('onCloseLayer')
      }, 100)
    },
    onImageProcessing (file) {
      this.hasImage = false

      if (!file.dataURL || file.dataURL.indexOf('http') !== 0) {
        this.imageProcess = 5
        this.fileName = file.name
        this.imageTimer = setInterval(() => {
          if (this.imageProcess < 99) {
            this.imageProcess++
          }
        }, 100)

        const resizeWidth = this.thumbnailSize[this.device].width
        const resizeHeight = this.thumbnailSize[this.device].height

        let reader = new FileReader()
        reader.addEventListener('load', (e) => {
          let origImg = new Image()

          origImg.src = e.target.result

          if (origImg.src.indexOf('data:image/') === -1) {
            clearInterval(this.imageTimer)
            this.imageProcess = -1
            this.hasImage = false
            return false
          } else {
            origImg.addEventListener('load', (event) => {
              const width = event.target.width
              const height = event.target.height
              const ratio = resizeWidth / width

              let canvas = document.createElement('canvas')
              canvas.width = resizeWidth
              canvas.height = resizeHeight
              let ctx = canvas.getContext('2d')

              let dw = width * ratio
              let dh = height * ratio
              let dx = 0
              let dy = 0
              if (dh < resizeHeight) {
                dy = (resizeHeight - dh) / 2

                const ratioCover = (resizeHeight + 60) / height
                let dwCover = width * ratioCover
                let dhCover = height * ratioCover
                let dxCover = (resizeWidth - dwCover) / 2 - 15
                let dyCover = (resizeHeight - dhCover) / 2 - 15
                ctx.filter = 'blur(15px)'
                ctx.drawImage(origImg, dxCover, dyCover, dwCover, dhCover)
                ctx.fillStyle = 'rgba(0, 0, 0, 0.35)'
                ctx.fillRect(-15, -15, resizeWidth + 30, resizeHeight + 30)
              }

              ctx.filter = 'none'
              ctx.drawImage(origImg, dx, dy, dw, dh)

              const base64ToFile = (dataURI, origFile) => {
                let byteString, mimestring
                if (dataURI.split(',')[0].indexOf('base64') !== -1) {
                  byteString = atob(dataURI.split(',')[1])
                } else {
                  byteString = decodeURI(dataURI.split(',')[1])
                }
                mimestring = dataURI.split(',')[0].split(':')[1].split(';')[0]
                let content = []
                for (let i = 0; i < byteString.length; i++) {
                  content[i] = byteString.charCodeAt(i)
                }
                let newFile = new File(
                  [new Uint8Array(content)], origFile.name, { type: mimestring }
                )
                let origProps = [
                  'upload', 'status', 'previewElement', 'previewTemplate', 'accepted'
                ]
                origProps.forEach(p => {
                  newFile[p] = origFile[p]
                })

                return newFile
              }

              const dropzone = this.$refs.dropzone.dropzone
              const canvasData = canvas.toDataURL()
              const resizedFile = base64ToFile(canvasData, file)
              const origFileIndex = dropzone.files.indexOf(file)
              dropzone.files[origFileIndex] = resizedFile
              setTimeout(() => {
                dropzone.emit('thumbnail', file, canvasData)
                this.hasImage = true
              }, 300)
            })
          }
        })
        reader.readAsDataURL(file)
      } else {
        clearInterval(this.imageTimer)
        this.imageProcess = 101
      }
    },
    onImageRemoved (file, xhr, formData) {
      this.imageProcess = 0
      this.fileName = ''
      clearInterval(this.imageTimer)
      this.imageTimer = null
      this.hasImage = false
      this.$refs.dropzone.dropzone.removeAllFiles()
    },
    onImageProcessed (file, dataUrl) {
      if (this.imageProcess < 101) {
        this.imageProcess = 100
      }
      setTimeout(() => {
        this.imageProcess = 101
      }, 400)
      clearInterval(this.imageTimer)
      this.imageTimer = null
    },
    onStopProcessing () {
      this.imageProcess = 0
      this.fileName = ''
      clearInterval(this.imageTimer)
      this.imageTimer = null
      this.$refs.dropzone.dropzone.removeAllFiles()
      this.hasImage = false
    },
    onMaxFilesExceeded (file) {
      this.imageProcess = 100
      setTimeout(() => {
        this.imageProcess = 101
      }, 400)
      this.fileName = ''
      clearInterval(this.imageTimer)
      this.imageTimer = null
      this.$refs.dropzone.dropzone.removeFile(file)
    },
    onUploading (file, xhr, formData) {
      const data = this.computedData
      formData.append('sid', data.sid)
      formData.append('urlId', data.urlId)
      formData.append('matchType', data.matchType)
      formData.append('device', this.device)
      formData.append('serviceCode', 1)
      formData.append('url', data.url)
    },
    onUpload () {
      this.capturing = true
      this.step = 3
      this.$refs.dropzone.processQueue()
      // const key = this.pageData.key || `${this.computedData.urlId}-${this.device}-${this.computedData.sid}`
      this.$emit('onCaptureProcessing', this.computedData)
      setTimeout(this.onCloseLayer, 5000)
    },
    captureEnded (index) {
      let original = this.wholeCaptureProcessing
      original.splice(index, 1)
      this.$emit('onWholeCaptureProcessingChange', original)
    },
    resizeTitleUrl () {
      const titleWidth = this.$refs.title.offsetWidth + 20 + 12
      this.$refs.url.style.maxWidth = `calc(100% - ${titleWidth}px)`
    }
  },
  created () {
    this.isOpen = true
    if (this.captureType === 'upload' && this.pageData.baApply.state !== 'run') {
      this.onSelectType('upload')
    } else if (this.captureType) {
      this.onSelectType('url')
    }
  },
  mounted () {
    if (this.captureType === 'upload') {
      const dropzone = this.$refs.dropzone.dropzone
      const mockFile = { name: '', dataURL: getS3Url(this.imagePath) }
      dropzone.files.push(mockFile)
      dropzone.emit('addedfile', mockFile)
      dropzone.emit('thumbnail', mockFile, mockFile.dataURL)
      dropzone.emit('complete', mockFile)
    }

    this.resizeTitleUrl()
  },
  props: {
    pageData: {
      required: true
    },
    capturingDevice: {
      required: false
    },
    wholeCaptureProcessing: {
      required: false
    }
  },
  data () {
    return {
      isOpen: false,
      step: 1,
      type: 'url',
      imageProcess: 0,
      imageTimer: null,
      fileName: '',
      urlType: 'default',
      bLogin: this.pageData.baApply ? this.pageData.baApply.bLogin : false,
      cssSelector: this.pageData.baApply ? this.pageData.baApply.exceptionSelector : '',
      excludeCheck: !!(this.pageData.baApply && !!this.pageData.baApply.exceptionSelector),
      hasImage: false,
      capturing: false,
      modelError: {
        captureUrl: ''
      },
      model: {
        captureUrl: ''
      },
      thumbnailSize: {
        desktop: {
          width: 600,
          height: 376
        },
        tablet: {
          width: 400,
          height: 530
        },
        phone: {
          width: 320,
          height: 510
        }
      },
      thumbnailRatio: {
        desktop: {
          width: 8,
          height: 5
        },
        tablet: {
          width: 5,
          height: 6
        },
        phone: {
          width: 2,
          height: 3
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-page-capture {
  @include positionXYcenter;
  padding: 30px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  transition: width 0.2s cubic-bezier(.28,.12,.42,.94),
              height 0.2s cubic-bezier(.28,.12,.42,.94);

  .close-layer {
    position: absolute;
    margin: 12px;
    top: 0;
    right: 0;
    background-position: center;
  }

  &.step1 {
    width: 370px;
    height: 297px;
  }

  &.step2 {
    width: 580px;
    height: 500px;
    &.heatmap {
      width: 640px;
      height: 560px;
      padding: 30px 0 25px;
    }
  }

  &.step3 {
    width: 370px;
    height: 270px;
  }

  .step-1 {
    @include positionXYcenter;
    width: 370px;
    height: 297px;
    text-align: center;

    .icon {
      margin-top: 20px;
    }

    h3 {
      font-size: 20px;
      font-weight: 700;
      color: #444444;
      margin-top: 15px;
    }

    h4 {
      font-size: 13px;
      font-weight: 700;
      color: #555555;
      margin-top: 28px;
    }

    p {
      font-size: 14px;
      line-height: 1.57;
      color: #666666;
      margin-top: 13px;
    }

    .buttons {
      margin-top: 40px;

      .ui-btn {
        margin: 0 2px 0 3px;
      }

      & .upload {
        &:hover {
          & /deep/ i {
            background-image: url(../../assets/img/icon--link-upload.svg)
          }
        }
      }
    }
  }

  .step-2 {
    @include positionXYcenter;
    width: 580px;
    height: calc(100% - 60px);
    &.heatmap {
      width: 100%;
    }
    .title--wrap {
      &.heatmap {
        padding: 0 30px;
      }
      h3 {
        display: inline-block;
        color: #2f2f2f;
        font-size: 15px;
        font-weight: 500;
      }
      .url {
        @include textEllipsis;
        margin-left: 20px;
        color: #767676;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        display: inline-block;
        vertical-align: bottom;
      }
    }

    .select-type {
      position: relative;
      height: 36px;
      width: 400px;
      border-radius: 20px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;
      margin: 24px 0 36px;
      &.heatmap {
        margin-left: 30px;
      }

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 215px;
        height: 38px;
        margin: -1px;
        border-radius: 20px;
        box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
        background-color: #ec0047;
        transition: left 0.2s cubic-bezier(.28,.12,.42,.94);
      }

      &.right {
        &::before {
          left: 50%;
        }
      }

      & > button {
        position: relative;
        display: inline-block;
        height: 36px;
        line-height: 36px;
        text-align: center;
        width: 50%;
        background-color: transparent;
        cursor: pointer;
        font-size: 15px;
        color: #888888;
        transition: color 0.2s ease;

        &.disabled {
          cursor: initial;
        }
      }

      .icon {
        vertical-align: middle;
        margin-top: -3px;
        width: 0;
        transition: width 0.2s cubic-bezier(.28,.12,.42,.94),
                    margin-right 0.2s cubic-bezier(.28,.12,.42,.94);
      }

      .on {
        color: #ffffff;
        padding-left: 42px;

        .icon {
          position: absolute;
          left: 12px;
          top: 11px;
          width: 20px;
        }
      }
      .disabled {
        opacity: 0.3;
      }
    }

    .capture-type {
      position: relative;
      height: 364px;
      &.heatmap {
        height: 372px;
      }

      &:before {
        content: "";
        position: absolute;
        top: 0;
        z-index: 9;
        left: 4px;
        width: calc(100% - 20px);
        height: 23px;
        background-image: linear-gradient(to top, rgba(255, 255, 255, 0.00) 0%, #FFF 64.06%);;
      }

      &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: calc(100% - 20px);
        height: 24px;
        background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
      }

      .url {
        position: absolute;
        width: 100%;

        .desc {
          margin: 14px 5px;
          font-size: 14px;
          line-height: 1.57;
          color: #666666;
          height: 88px;
          text-align: center;
        }

        .set-url {
          text-align: center;
          margin: 58px 0;
          padding: 0 50px;
          border-top: dashed 1px #d7d7d7;

          label {
            font-size: 14px;
            line-height: 1.57;
            color: #666666;
            padding: 22px 15px 9px 15px;
            display: inline-block;
            cursor: pointer;

            .icon {
              vertical-align: middle;
              margin-top: -2px;
            }
          }

          .input--wrap.above-input {
            margin-top: 0;
          }
        }

        .buttons {
          padding-top: 46px;
          text-align: center;

          .ui-btn {
            min-width: 90px;
          }
        }
      }

      .upload {
        position: absolute;
        width: 100%;
        margin-top: -6px;

        .dropzone {
          position: relative;
          height: 100%;
          min-height: 201px;
        }

        .upload-area {
          border-radius: 10px;
          border: solid 1px #d7d7d7;
          height: 345px;

          .format {
            height: 145px;
            border-bottom: 1px dashed #d7d7d7;
            position: relative;

            .extension {
              @include positionYcenter;
              margin-left: 75px;

              h4 {
                font-size: 13px;
                font-weight: 700;
                color: #444444;
                margin-bottom: 10px;
              }

              ul {
                font-size: 13px;
                color: #444444;

                li {
                  color: #666666;

                  &::before {
                    content: "";
                    width: 4px;
                    height: 4px;
                    background: #666666;
                    display: inline-block;
                    vertical-align: middle;
                    border-radius: 10px;
                    margin: -2px 4px 0;
                    transform: scale(0.75);
                  }
                }
              }
            }

            .size {
              @include positionYcenter;
              right: 60px;
              text-align: center;
              text-shadow: 0 1px 1px rgba(0, 0, 0, 0.25), 0 0 1px rgba(0, 0, 0, 0.16);
              font-size: 13px;
              line-height: 1.15;
              color: #ffffff;
              width: 180px;
              height: 130px;

              p {
                @include positionXYcenter;
                font-weight: 700;
              }

              &.desktop {
                background-image: url('../../assets/img/journey/resize-desktop.svg');

                p {
                  padding-right: 8px;
                  padding-bottom: 5px;
                }
              }

              &.tablet {
                background-image: url('../../assets/img/journey/resize-tablet.svg');

                p {
                  padding-bottom: 5px;
                }
              }

              &.phone {
                background-image: url('../../assets/img/journey/resize-phone.svg');

                p {
                  padding-bottom: 7px;
                }
              }
            }
          }

          .drop-area {
            cursor: grab;
            height: 199px;
            text-align: center;
            background-color: #f4f4f4;
            border-radius: 0 0 9px 9px;
            position: relative;

            .drop {
              @include positionXYcenter;
              margin-top: -3px;

              p {
                margin: 8px 0;
                font-size: 14px;
                font-weight: 700;
                line-height: 1.57;
                color: #0e90a5;
              }
            }
          }

          .image-processing {
            position: absolute;
            top: 0px;
            bottom: 1px;
            left: 1px;
            cursor: grab;
            height: 199px;
            width: calc(100% - 2px);
            text-align: center;
            background-color: #f4f4f4;
            border-radius: 0 0 9px 9px;
            transition: opacity 0.2s ease;

            .loader {
              position: absolute;
              top: 50%;
              left: 50%;
              width: 340px;
              height: 10px;
              border-radius: 5px;
              background-color: #d7d7d7;
              margin: 10px auto 0;
              transform: translate(calc(-50% - 15px), -50%);

              .bar {
                position: absolute;
                top: 0;
                left: 0;
                min-width: 10px;
                height: 10px;
                border-radius: 5px;
                background-color: #ec0047;
                transition: width 0.3s ease;
              }

              .value {
                position: absolute;
                top: -85px;
                left: 0;
                text-align: center;
                transform: translateX(-50%);
                transition: left 0.3s ease;

                span {
                  display: block;
                  color: #ec0047;
                  font-size: 13px;
                  margin-top: 21px;
                }
              }
            }

            .close {
              @include positionYcenter;
              right: -30px;
              width: 12px;
              height: 12px;
              padding: 3px;
              border: solid 1px #bbbbbb;
              background-color: #ffffff;
              border-radius: 10px;
              background-size: 10px 10px;

              &.errorClose {
                top: 12px;
                z-index: 99999;
              }
            }

            .file-name {
              @include positionXcenter;
              bottom: 33px;
              color: #222222;
              font-size: 12px;
              width: 100%;
            }

            .error-message {
              @include positionXYcenter;
              top: 50%;
              bottom: 33px;
              color: #222222;
              font-size: 12px;
              color: #ec0047;
              width: 100%;

              p {
               padding-bottom: 20px;
              }
            }
          }
        }

        & /deep/ .dz-preview {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 10px;
          background-color: #333333;
          text-align: center;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.2s ease;

          .dz-image {
            @include positionXYcenter;
            max-height: 227px;
            overflow: hidden;
            border-radius: 8px;
            box-shadow: 0 12px 13px -7px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.15);
            border: solid 2px #ffffff;
            background-color: #ffffff;

            img {
              width: 100%;
              border-radius: 7px;
              margin-bottom: -3px;
            }
          }

          .dz-remove {
            position: absolute;
            bottom: 7px;
            right: 7px;
            color: transparent;
            background-image: url('../../assets/img/journey/image-remove.svg');
            background-position: center center;
            width: 32px;
            height: 32px;
            border-radius: 5px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
            background-color: #ffffff;
            background-repeat: no-repeat;
          }

          .dz-error-message,
          .dz-error-mark,
          .dz-success-mark,
          .dz-details {
            display: none;
          }
        }

        .buttons {
          padding-top: 25px;
          text-align: center;

          .ui-btn {
            min-width: 90px;
          }
        }

        &.desktop /deep/ .dz-image{
          width: 350px;
        }

        &.tablet /deep/ .dz-image{
          width: 171px;
        }

        &.phone /deep/ .dz-image{
          width: 143px;
        }

        &.process-0 /deep/ .image-processing,
        &.process-101 /deep/ .image-processing {
          pointer-events: none;
          opacity: 0;
        }

        &.process-101 /deep/ .dz-preview {
          pointer-events: auto;
          opacity: 1 !important;
        }
      }

    }

    .capture-buttons {
      position: absolute;
      text-align: center;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 0);
      &.heatmap {

      }
    }
  }

  .step-3 {
    @include positionXYcenter;
    width: 370px;
    height: 270px;

    .capturing {
      text-align: center;

      .ba-loading {
        margin: 20px 0 10px;
      }

      .title {
        font-size: 20px;
        color: #ec0047;
      }

      .desc {
        margin-top: 30px;
        font-size: 13px;
        line-height: 1.54;
        color: #444444;
      }

      .loading--wrap {
        width: 180px;
        height: 12px;
        margin: 17px auto 28px;

        .loading-bar {
          display: block;
          position: absolute;
          padding: 6px;
          border-radius: 50%;
          background-color: $brandColor;
          animation: loading 1.4s ease-in-out 0s infinite;

          &:nth-child(1) {
            animation-delay: 0s;
          }

          &:nth-child(2) {
            animation-delay: 0.15s;
          }

          &:nth-child(3) {
            animation-delay: 0.3s;
          }

          &:nth-child(4) {
            animation-delay: 0.45s;
          }

          &:nth-child(5) {
            animation-delay: 0.6s;
          }
        }
      }
    }
  }
}

.fade-leave-active,
.fade-enter-active {
  transition: opacity 0.3s ease;
  opacity: 1;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@keyframes loading {
  0% {
    transform: translateX(-15px);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(180px);
    opacity: 0;
  }
}
</style>
